/* eslint-disable */
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React context
import { useMaterialUIController, setTransparentNavbar, setMiniSidenav } from "context";
import jwtDecode from "jwt-decode";
import Sessions from "utils/Sessions";
import Constants, { defaultData, Colors } from "utils/Constants";
import Confirmation from "examples/modal/Confirmation/Confirmation";
import { resetStateThunk } from "redux/Thunks/Authentication";

const adminOptions = [
  { name: "Banks", link: "/client/lead-utils-banks" },
  { name: "Codes", link: "/client/lead-utils-codes" },
  { name: "Sales Manager", link: "/client/lead-utils-sales-manager" },
  { name: "Profile", link: "/client/profile" },
];

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { transparentNavbar, fixedNavbar, darkMode, miniSidenav } = controller;
  const [role, setRole] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const dispatchThunk = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const profileOpen = Boolean(profileAnchorEl);

  const [openLogout, setOpenLogout] = useState(false);

  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = (url) => {
    if (url) navigate(url);
    setProfileAnchorEl(null);
  };

  const handleOpenLogout = () => {
    setProfileAnchorEl(null);
    setOpenLogout(true);
  };

  const handleLogout = async () => {
    setOpenLogout(false);
    Sessions.setClear();
    navigate("/authentication/sign-in", { replace: true });
    await dispatchThunk(resetStateThunk());
  };

  const handleSettingsClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = (url) => {
    if (url) navigate(url);
    setAnchorEl(null);
  };

  useEffect(() => {
    if (Sessions.userToken) {
      try {
        const decodedToken = jwtDecode(Sessions.userToken);
        setRole(decodedToken?.role);
      } catch (e) {
        setRole("");
      }
    }
  }, []);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  // Dynamic Breadcrumb Title generator based on Figma
  const getBreadcrumbTitle = (path) => {
    if (path.includes("/client/bank-account-opening")) {
      return { parent: "Lead Bay", current: "Bank Account Opening", count: "142" };
    }
    if (path.includes("/client/lead-bay")) {
      return { parent: "Lead Bay", current: "Loans & Credit Cards", count: "" };
    }
    if (path.includes("/client/insurance")) {
      return { parent: "Lead Bay", current: "Insurance", count: "" };
    }
    if (path.includes("/client/bill")) {
      return { parent: "Lead Bay", current: "Utility Bill Payments", count: "" };
    }
    if (path.includes("/client/about-us")) {
      return { parent: "", current: "About Us", count: "" };
    }
    if (path.includes("/client/mobileusers")) {
      return { parent: "", current: "Mobile Users", count: "" };
    }
    if (path.includes("/client/partners")) {
      return { parent: "", current: "Partners", count: "" };
    }
    if (path.includes("/client/credit-score")) {
      return { parent: "", current: "Check Credit Score", count: "" };
    }
    return { parent: "Dashboard", current: "Overview", count: "" };
  };

  const breadcrumb = getBreadcrumbTitle(location.pathname);

  const iconBtnStyle = {
    backgroundColor: "#F2F4F7",
    border: "1px solid #EAECF0",
    borderRadius: "50%",
    width: 36,
    height: 36,
    color: "#344054",
    "&:hover": {
      backgroundColor: "#E4E7EC",
    },
  };

  const userName = Sessions.fullName || "Mr. Rajen Shah";
  const userInitial = userName.charAt(0).toUpperCase() || "R";
  const primaryTableColor = Colors?.PRIMARY || "#043b9f";

  return (
    <>
      <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={{
        backgroundColor: "#FFFFFF !important",
        boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)",
        borderBottom: "1px solid #EAECF0",
        py: 1,
        px: { xs: 1, md: 3 },
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          px: 0,
        }}
      >
        {/* Left Side: Mobile Menu Button & Clean Text Breadcrumb */}
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            size="small"
            color="inherit"
            aria-label="open sidebar"
            sx={{
              mr: 1,
              display: { xs: "inline-flex", md: "none" },
              border: "1px solid #d7dbe8",
              borderRadius: "8px",
              backgroundColor: "#ffffff",
              color: "#1f2937",
            }}
            onClick={handleMiniSidenav}
          >
            <MenuIcon fontSize="small" />
          </IconButton>

          {/* Text-based Breadcrumb matching Figma */}
          <Box display="flex" alignItems="center" gap={0.8}>
            {breadcrumb.parent && (
              <>
                <Typography fontSize="14px" color="#667085" fontWeight="400">
                  {breadcrumb.parent}
                </Typography>
                <Typography fontSize="14px" color="#98A2B3" fontWeight="400">
                  /
                </Typography>
              </>
            )}
            <Typography fontSize="15px" color="#101828" fontWeight="600">
              {breadcrumb.current}
            </Typography>
            {breadcrumb.count && (
              <Typography
                fontSize="12px"
                color="#667085"
                fontWeight="500"
                sx={{ ml: 0.5, backgroundColor: "#F2F4F7", px: 0.8, py: 0.2, borderRadius: "12px" }}
              >
                {breadcrumb.count}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right Side: Static Country Flag & Language Pill, Settings, Bell, & Profile */}
        <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} ml="auto">
          {/* Static Country Flag & Language Indicator (No Dropdown) */}
          <Box
            display="flex"
            alignItems="center"
            gap={0.8}
            px={1.5}
            py={0.5}
            borderRadius="20px"
            sx={{
              border: "1px solid #D0D5DD",
              backgroundColor: "#FFFFFF",
              userSelect: "none",
            }}
          >
            <Box
              component="img"
              src="https://miniopic.finvibespro.net/development/Common/FO7qLws2S3jZywa_Indian Flag.png"
              alt="India Flag"
              sx={{ width: 18, height: 13, borderRadius: "2px", objectFit: "cover" }}
            />
            <Typography fontSize="13px" fontWeight="500" color="#344054">
              English
            </Typography>
          </Box>

          {/* Settings Button */}
          {role === defaultData.SUPER_ADMIN_ROLE && (
            <IconButton onClick={handleSettingsClick} sx={iconBtnStyle}>
              <SettingsOutlinedIcon sx={{ fontSize: 19 }} />
            </IconButton>
          )}

          {/* Notification Bell Button */}
          <IconButton onClick={(e) => handleSettingsClick(e)} sx={iconBtnStyle}>
            <NotificationsNoneOutlinedIcon sx={{ fontSize: 19 }} />
          </IconButton>

          {/* Profile User Section with Avatar matching Primary Table Header Color */}
          <Box
            display="flex"
            alignItems="center"
            gap={1.2}
            onClick={handleProfileClick}
            sx={{ cursor: "pointer", ml: 0.5 }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                backgroundColor: `${primaryTableColor} !important`,
                fontSize: "14px",
                fontWeight: "600",
                color: "#FFFFFF",
              }}
            >
              {userInitial}
            </Avatar>
            <Box display={{ xs: "none", sm: "flex" }} flexDirection="column">
              <Typography variant="caption" fontSize="11px" color="#667085" fontWeight="400">
                Hey, Welcome!
              </Typography>
              <Box display="flex" alignItems="center" gap={0.3}>
                <Typography variant="body2" fontSize="13px" color="#101828" fontWeight="600">
                  {userName}
                </Typography>
                <KeyboardArrowDownIcon sx={{ fontSize: 16, color: "#667085" }} />
              </Box>
            </Box>
          </Box>

          {/* Settings Dropdown Menu */}
          {role === defaultData.SUPER_ADMIN_ROLE && (
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={() => handleSettingsClose(null)}
              PaperProps={{ style: { width: "180px", marginTop: "8px" } }}
            >
              {adminOptions.map((option) => (
                <MenuItem key={option.name} onClick={() => handleSettingsClose(option.link)}>
                  {option.name}
                </MenuItem>
              ))}
            </Menu>
          )}

          {/* Profile Dropdown Menu */}
          <Menu
            anchorEl={profileAnchorEl}
            open={profileOpen}
            onClose={() => handleProfileClose(null)}
            PaperProps={{ style: { width: "180px", marginTop: "8px" } }}
          >
            <MenuItem onClick={() => handleProfileClose("/client/profile")}>
              My Profile
            </MenuItem>
            <MenuItem onClick={handleOpenLogout}>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>

    {openLogout && (
      <Confirmation
        open={openLogout}
        title={Constants.USER_LOGOUT_TITTLE}
        message={Constants.LOGOUT_MESSAGE}
        handleClose={() => setOpenLogout(false)}
        handleAction={handleLogout}
      />
    )}
  </>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
